import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Handshake, TrendingUp, Lightbulb, Check, PartyPopper, NotebookPen, ChevronDown } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Avatar, Help, rise, SKILL_STYLE, SKILL_LABEL } from './shared';
import { ActionDialog } from './GoalsTab';

const monthLabel = (m) => {
  try { return new Date(`${m}-01T12:00:00`).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }); } catch { return m; }
};

function SectionTitle({ children }) {
  return <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{children}</p>;
}

export default function MonthlyTab({ meId }) {
  const [data, setData] = useState(null);
  const [takeaway, setTakeaway] = useState('');
  const [teamNotes, setTeamNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(() => {
    api.teamDevMonthlyContext().then((res) => {
      setData(res.data);
      setTakeaway(res.data.my_takeaway || '');
      setTeamNotes(res.data.round?.team_notes || '');
    }).catch(() => toast.error('Monats-Runde konnte nicht geladen werden'));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  const round = data.round;
  const open = round?.status === 'open';

  const start = async () => {
    try { await api.teamDevMonthlyStart(); toast.success('Runde gestartet — das Team wurde eingeladen. 🤝'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Das hat nicht geklappt'); }
  };

  const saveTakeaway = async () => {
    if (!takeaway.trim()) return toast('Ein Satz reicht — was nimmst du aus der Runde mit?');
    setSaving(true);
    try { await api.teamDevMonthlyTakeaway(round.id, takeaway.trim()); toast.success('Dein Takeaway ist notiert — nur für dich. 🌱'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Speichern hat nicht geklappt'); }
    finally { setSaving(false); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try { await api.teamDevMonthlyUpdate(round.id, { team_notes: teamNotes }); toast.success('Team-Beschlüsse gespeichert.'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Speichern hat nicht geklappt'); }
    finally { setSaving(false); }
  };

  const closeRound = async () => {
    try { await api.teamDevMonthlyUpdate(round.id, { action: 'close', team_notes: teamNotes }); toast.success('Runde abgeschlossen — bis nächsten Monat! 🤝'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Das hat nicht geklappt'); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="td-monthly">
      <div className="td-rise bg-card border border-border rounded-lg p-5 flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-full bg-emerald-600/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
          <Handshake className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-medium text-foreground">Monats-Runde · {monthLabel(data.month)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {open ? 'Die Runde läuft — hier ist eure Agenda fürs Zusammenkommen.'
              : round ? 'Diese Runde ist abgeschlossen.'
                : 'Einmal im Monat kommen wir als Team zusammen und sprechen — diese Agenda entsteht automatisch aus eurem Monat.'}
          </p>
        </div>
        {!round && data.is_admin && (
          <Button onClick={start} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" data-testid="td-monthly-start">
            <Handshake className="w-4 h-4 mr-1.5" /> Runde starten
          </Button>
        )}
        {round && (
          <span className={`text-[9px] font-mono uppercase border rounded px-2 py-1 ${open ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400' : 'border-border text-muted-foreground'}`}>
            {open ? 'läuft' : 'abgeschlossen'}
          </span>
        )}
      </div>

      {!round && !data.is_admin && (
        <div className="bg-card border border-dashed border-border rounded-lg p-8 text-center td-rise" data-testid="td-monthly-waiting">
          <p className="text-2xl mb-2">🤝</p>
          <p className="text-sm text-foreground">Noch keine Runde für diesen Monat gestartet.</p>
          <p className="text-xs text-muted-foreground mt-1">Sobald es losgeht, findet ihr hier die gemeinsame Agenda. Unten siehst du schon, was der Monat gebracht hat.</p>
        </div>
      )}

      <div className="space-y-2.5">
        <SectionTitle>1 · Rückblick — die Highlights des Monats</SectionTitle>
        {data.highlights.length === 0 && data.kudos.length === 0 ? (
          <p className="text-xs text-muted-foreground bg-card border border-border rounded-lg px-4 py-3">Noch keine Highlights diesen Monat — die Check-ins füllen diesen Teil.</p>
        ) : (
          <div className="space-y-1.5">
            {data.highlights.map((h, i) => (
              <div key={i} style={rise(i)} className="td-rise bg-card border border-border rounded-lg px-4 py-2.5 flex gap-2.5 items-start" data-testid={`td-monthly-highlight-${i}`}>
                <span>🌟</span>
                <p className="text-xs text-foreground/90"><span className="font-medium">{h.user_name}</span> <span className="font-mono text-[9px] text-muted-foreground">{h.week_key}</span><br />{h.text}</p>
              </div>
            ))}
            {data.kudos.map((k, i) => (
              <div key={`k${i}`} className="td-rise bg-card border border-border rounded-lg px-4 py-2.5 flex gap-2.5 items-start" data-testid={`td-monthly-kudos-${i}`}>
                <span>💛</span>
                <p className="text-xs text-foreground/90"><span className="font-medium">{k.from_name}</span> → <span className="font-medium">{k.to_name}</span><br />{k.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2.5">
        <SectionTitle>2 · Zielkarten-Runde — jede:r erzählt 2 Minuten</SectionTitle>
        {data.goals.length === 0 ? (
          <p className="text-xs text-muted-foreground bg-card border border-border rounded-lg px-4 py-3">Keine aktiven Ziele — vielleicht entsteht in der Runde das erste?</p>
        ) : data.goals.map((g, i) => (
          <div key={g.id} style={rise(i)} className="td-rise td-card bg-card border border-border rounded-lg p-4 flex items-center gap-3 flex-wrap" data-testid={`td-monthly-goal-${i}`}>
            <span className="text-xl td-plant">{g.stage}</span>
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm text-foreground flex items-center gap-2 flex-wrap">
                <Avatar name={g.owner_name} className="w-5 h-5 text-[9px]" />
                <span className="font-medium">{g.owner_name}</span>
                <span className={`text-[8px] font-mono uppercase border rounded-full px-1.5 py-px ${SKILL_STYLE[g.skill_type]}`}>{SKILL_LABEL[g.skill_type]}</span>
              </p>
              <p className="text-sm text-foreground/90 mt-1">{g.title}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Diesen Monat: {g.month_signals} 📈 · {g.month_ideas} 💡</p>
            </div>
            {!g.is_mine && open && (
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => setDialog({ goal: g, kind: 'signal' })} data-testid={`td-monthly-signal-${i}`}
                  className="h-7 text-xs rounded-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300">
                  <TrendingUp className="w-3 h-3 mr-1" /> 📈
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDialog({ goal: g, kind: 'idea' })} data-testid={`td-monthly-idea-${i}`}
                  className="h-7 text-xs rounded-full border-violet-500/30 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300">
                  <Lightbulb className="w-3 h-3 mr-1" /> 💡
                </Button>
              </div>
            )}
          </div>
        ))}
        {open && <Help>Während ihr sprecht: Signale 📈 und Ideen 💡 direkt hier erfassen — so geht nichts verloren.</Help>}
      </div>

      {data.completed.length > 0 && (
        <div className="space-y-2.5">
          <SectionTitle>3 · Geschafft diesen Monat 🎉</SectionTitle>
          {data.completed.map((g, i) => (
            <div key={g.id} className="td-rise bg-card border border-emerald-500/25 rounded-lg px-4 py-3" data-testid={`td-monthly-completed-${i}`}>
              <p className="text-sm text-foreground"><PartyPopper className="w-3.5 h-3.5 text-emerald-400 inline mr-1.5" /><span className="font-medium">{g.owner_name}</span>: {g.title}</p>
              {g.closing_note && <p className="text-xs text-emerald-400/90 mt-1">💚 Was geholfen hat: <span className="text-foreground/80">{g.closing_note}</span></p>}
            </div>
          ))}
        </div>
      )}

      {data.blockers.length > 0 && (
        <div className="space-y-2.5">
          <SectionTitle>{data.completed.length > 0 ? '4' : '3'} · Blocker des Monats — wo helfen wir?</SectionTitle>
          {data.blockers.map((b, i) => (
            <div key={i} className="td-rise bg-card border border-sky-500/20 rounded-lg px-4 py-2.5" data-testid={`td-monthly-blocker-${i}`}>
              <p className="text-xs text-foreground/90"><span className="font-medium">{b.user_name}</span> <span className="font-mono text-[9px] text-muted-foreground">{b.week_key}</span><br />{b.text}</p>
            </div>
          ))}
        </div>
      )}

      {round && (
        <>
          <div className="td-rise bg-card border border-border rounded-lg p-5 space-y-2.5" data-testid="td-monthly-takeaway-box">
            <p className="text-sm font-medium text-foreground flex items-center gap-2"><NotebookPen className="w-4 h-4 text-emerald-400" /> Mein Takeaway aus der Runde</p>
            <Textarea value={takeaway} onChange={(e) => setTakeaway(e.target.value)} rows={2}
              placeholder="Was nimmst du aus dem Gespräch mit? Nur du siehst das."
              className="text-sm bg-background rounded-md" data-testid="td-monthly-takeaway-input" disabled={!open && !takeaway} />
            {open && (
              <Button size="sm" onClick={saveTakeaway} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-7 text-xs" data-testid="td-monthly-takeaway-save">
                <Check className="w-3 h-3 mr-1" /> Speichern
              </Button>
            )}
          </div>

          <div className="td-rise bg-card border border-border rounded-lg p-5 space-y-2.5" data-testid="td-monthly-notes-box">
            <p className="text-sm font-medium text-foreground">Team-Beschlüsse & Erkenntnisse</p>
            {data.is_admin && open ? (
              <>
                <Textarea value={teamNotes} onChange={(e) => setTeamNotes(e.target.value)} rows={3}
                  placeholder="Was haben wir beschlossen? Was nehmen wir als Team mit?"
                  className="text-sm bg-background rounded-md" data-testid="td-monthly-notes-input" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveNotes} disabled={saving} variant="outline" className="rounded-full h-7 text-xs" data-testid="td-monthly-notes-save">Speichern</Button>
                  <Button size="sm" onClick={closeRound} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-7 text-xs" data-testid="td-monthly-close">
                    <Check className="w-3 h-3 mr-1" /> Runde abschließen
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-foreground/85 whitespace-pre-wrap">{round.team_notes || 'Noch keine Beschlüsse notiert.'}</p>
            )}
          </div>
        </>
      )}

      {data.past_rounds.length > 0 && (
        <div>
          <button onClick={() => setShowPast((s) => !s)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="td-monthly-past-toggle">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPast ? '' : '-rotate-90'}`} />
            Frühere Runden ({data.past_rounds.length})
          </button>
          {showPast && (
            <div className="mt-2 space-y-2">
              {data.past_rounds.map((p, i) => (
                <div key={p.id} className="bg-card border border-border rounded-lg p-4" data-testid={`td-monthly-past-${i}`}>
                  <p className="text-xs font-medium text-foreground">{monthLabel(p.month)} <span className="text-[9px] font-mono text-muted-foreground ml-1">{p.status === 'closed' ? 'abgeschlossen' : 'offen'}</span></p>
                  {p.team_notes && <p className="text-xs text-foreground/80 mt-1 whitespace-pre-wrap">🤝 {p.team_notes}</p>}
                  {p.my_takeaway && <p className="text-xs text-emerald-400/90 mt-1">🌱 Dein Takeaway: <span className="text-foreground/80">{p.my_takeaway}</span></p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {dialog && <ActionDialog goal={dialog.goal} kind={dialog.kind} onClose={() => setDialog(null)} onDone={load} />}
    </div>
  );
}
